import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { globalAssistantPathsMatch } from './global-assistant-route-filter-path';

/**
 * The drawer's turn-continuation state machine: cross-navigation "pending
 * resume" state armed by an offer click and consumed by a location-keyed
 * effect once the router lands on the destination (so the resumed turn reads
 * the DESTINATION page context, not the page it left). Lives in a hook — same
 * component instance as the drawer — so the refs survive the navigate
 * re-render exactly as before the extraction.
 */
export function useGlobalAiTurnContinuation(args: {
  isTurnRunning: boolean;
  startAssistantTurn: (
    prompt: string,
    options?: { silent?: boolean; suppressReportOffer?: boolean }
  ) => Promise<void>;
  transcriptScrollRoot: HTMLDivElement | null;
}) {
  const location = useLocation();

  // A nav-confirm chip click navigates, then resumes the chained task ON the
  // destination page. React Router's `navigate()` does NOT update `location`
  // synchronously, so the resume CANNOT fire in the click handler — it would
  // run `buildTurnContext` against the page the user just LEFT (wrong
  // pageKind/taxonomy/canFilter/filter-context). Instead we ARM a pending
  // resume here and fire it from a `location.pathname`-keyed effect once the
  // router has actually landed on `expectedPath`. A ref (not state) avoids a
  // stale-closure double-fire across the navigation re-render.
  const pendingNavResumeRef = useRef<{
    continuationPrompt: string;
    pageName: string;
    expectedPath: string;
  } | null>(null);

  // Confirm-navigate-resume for `create_custom_page`. Clicking the build chip
  // creates an empty draft + navigates to it WITHOUT emitting the build, then
  // ARMS this ref keyed on the draft path. A `location.pathname`-keyed effect
  // fires the resume once the router lands on the empty draft, seeding the loop
  // with the full original prompt + a "you're on a new empty page, build it
  // here by calling create_custom_page" system note. Mirrors
  // `pendingNavResumeRef`, but carries a fully-formed `seed` because the build
  // resume needs a different (explicit "build here") instruction.
  const pendingCreatePageResumeRef = useRef<{
    seed: string;
    expectedPath: string;
  } | null>(null);

  // Confirmed-build signal for `create_custom_page`. The tool OFFERS a
  // "Build the dashboard" chip by default (terminal). Clicking the chip sets
  // this ref true (alongside navigating to the empty draft + arming the
  // resume), so on the resumed turn the loop's `create_custom_page` handle runs
  // in BUILD mode: it emits the CREATE page-gen on the CURRENT draft (no nav, no
  // new draft) and collects widget digests for a grounded summary, rather than
  // offering again. The runner resets it on consumption. Shared with the turn
  // runner via deps.
  const pendingCustomPageBuildRef = useRef(false);

  // Fire a pending nav-confirm resume ONCE the router has actually landed on
  // the destination. Keyed on `location.pathname` so it runs after the
  // navigation re-render — at which point `buildTurnContext` reads the
  // destination page's context, not the page we left. Guarded so it fires
  // exactly once (clears the ref before starting) and never while a turn is
  // already running.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally keyed on location.pathname; startAssistantTurn/isTurnRunning are read at fire time
  useEffect(() => {
    const pending = pendingNavResumeRef.current;
    if (!pending || args.isTurnRunning) {
      return;
    }
    if (!globalAssistantPathsMatch(location.pathname, pending.expectedPath)) {
      return;
    }
    pendingNavResumeRef.current = null;
    void args.startAssistantTurn(
      `[System: The user clicked the confirmation button and you have ALREADY navigated to ${pending.pageName}. The navigation is done — do NOT navigate again or offer another navigation button. If the request was only to open this page, briefly confirm you are here and end your turn; otherwise continue any remaining task on this page.] ${pending.continuationPrompt}`,
      { silent: true }
    );
  }, [location.pathname, args.isTurnRunning]);

  // Fire a pending create-page resume ONCE the router has landed on the empty
  // draft (mirrors the nav-confirm resume effect above). The seed was already
  // formed at click time (full original prompt + "build here" system note), so
  // the resumed turn lands on the empty editable draft and the model builds it
  // out via `update_current_page`.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally keyed on location.pathname; startAssistantTurn/isTurnRunning are read at fire time
  useEffect(() => {
    const pending = pendingCreatePageResumeRef.current;
    if (!pending || args.isTurnRunning) {
      return;
    }
    if (!globalAssistantPathsMatch(location.pathname, pending.expectedPath)) {
      return;
    }
    pendingCreatePageResumeRef.current = null;
    void args.startAssistantTurn(pending.seed, {
      silent: true,
      suppressReportOffer: true,
    });
    // Follow the build: jump the transcript to the bottom so the incoming
    // reasoning trace + grounded summary are in view as they stream.
    const transcriptScrollRoot = args.transcriptScrollRoot;
    if (transcriptScrollRoot) {
      requestAnimationFrame(() => {
        transcriptScrollRoot.scrollTop = transcriptScrollRoot.scrollHeight;
      });
    }
  }, [location.pathname, args.isTurnRunning]);

  return {
    pendingCustomPageBuildRef,
    armNavResume: (next: {
      continuationPrompt: string;
      pageName: string;
      expectedPath: string;
    }) => {
      pendingNavResumeRef.current = next;
    },
    armCreatePageResume: (next: { seed: string; expectedPath: string }) => {
      pendingCreatePageResumeRef.current = next;
    },
    armBuildHere: () => {
      pendingCustomPageBuildRef.current = true;
    },
  };
}
